/**
 * loginHS 이용 자격(2026-10-04) — 첫 사용부터 1개월 무료·기존 회원 올해 말까지·DocuAI 유료 구독이면 계속.
 *
 * 서버가 무료 기간이 끝난 사용자의 AI·대량 분류·관세사 이력·문서 인식 요청을 402(code=subscription_required)로 막는다.
 * 호출하는 곳이 여러 군데라, fetch 를 한 번 감싸 402 를 창 이벤트로 알린다(띠가 받아 안내한다). 응답 자체는 그대로 돌려준다.
 */
import { readToken } from "./authState";

export const SUBSCRIPTION_EVENT = "loginhs:subscription-required";
export const PRICING_URL = "/?page=pricing";

const STATE_LABEL = {
  trial: "무료 이용 기간",
  legacy: "기존 회원 무료 기간",
  extended: "연장된 이용 기간",
  paid: "구독",
};

export function stateLabel(state) {
  return STATE_LABEL[state] || "이용 기간";
}

export async function fetchEntitlement(fetchImpl = fetch) {
  const token = readToken();
  if (!token) return null;
  try {
    const res = await fetchImpl("/api/onto/entitlement/me", {
      headers: { Authorization: `Bearer ${token}` },
      cache: "no-store",
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (_e) {
    return null;
  }
}

/** 가입 뒤 마케팅 수신 동의 → 무료 체험 시작(약관 제7조 제2항). 새 상태를 돌려준다(실패하면 null). */
export async function consentAndStartTrial(fetchImpl = fetch) {
  const token = readToken();
  if (!token) return null;
  try {
    const res = await fetchImpl("/api/onto/entitlement/marketing-consent", {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.ok ? await res.json() : null;
  } catch (_e) {
    return null;
  }
}

/** 띠에 무엇을 보일지 — null 이면 띠 없음. */
export function bannerFor(ent) {
  if (!ent) return null;
  if (ent.state === "needs_consent" || ent.needs_consent) {
    return {
      kind: "consent",
      text: "마케팅 정보 수신에 동의하면 AI 더 묻기·PROVE 모델 제안·대량 분류·관세사 이력·문서 인식을 1개월 무료로 체험할 수 있습니다. 관세율표·검색은 지금도 무료입니다.",
    };
  }
  if (ent.allowed === false) {
    return {
      kind: "expired",
      text: "무료 이용 기간이 끝났습니다. 관세율표·검색은 계속 쓸 수 있고, AI·대량 분류·관세사 이력·문서 인식은 구독하면 다시 쓸 수 있습니다.",
    };
  }
  const days = ent.days_left;
  if (ent.state !== "paid" && typeof days === "number" && days <= 7) {
    return {
      kind: "soon",
      text: `${stateLabel(ent.state)}이 ${days}일 남았습니다(${ent.until}까지). 구독하면 끊김 없이 이어 쓸 수 있습니다.`,
    };
  }
  return null;
}

let installed = false;

/** 402(subscription_required)를 창 이벤트로 알리는 fetch 감싸기 — 한 번만 설치한다. */
export function installSubscriptionWatcher(win = typeof window !== "undefined" ? window : null) {
  if (!win || installed || typeof win.fetch !== "function") return;
  installed = true;
  const original = win.fetch.bind(win);
  win.fetch = async (...args) => {
    const res = await original(...args);
    if (res && res.status === 402) {
      try {
        const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";
        if (url.includes("/api/onto/")) {
          const body = await res.clone().json();
          if (body && body.code === "subscription_required") {
            win.dispatchEvent(new CustomEvent(SUBSCRIPTION_EVENT, { detail: body }));
          }
        }
      } catch (_e) {
        /* 본문이 JSON 이 아니면 무시 */
      }
    }
    return res;
  };
}

export function __resetWatcherForTest() {
  installed = false;
}

/**
 * 관세사 이력 서버(별도 서버)는 nginx 가 이 서버의 잠금 확인을 거쳐 보낸다 — 로그인 정보를 함께 보내야 한다.
 * 무료 기간이 끝났으면 403 이 온다.
 */
export function historyFetchInit(signal) {
  const token = readToken();
  return token ? { signal, headers: { Authorization: `Bearer ${token}` } } : { signal };
}

export const HISTORY_BLOCKED_TEXT = "무료 이용 기간이 끝나 관세사 이력은 구독 후 볼 수 있습니다.";

/** 이력 응답이 잠금(403)이면 띠에 알리고 true. */
export function historyBlocked(res, win = typeof window !== "undefined" ? window : null) {
  if (!res || res.status !== 403) return false;
  if (win) win.dispatchEvent(new CustomEvent(SUBSCRIPTION_EVENT, { detail: { code: "subscription_required" } }));
  return true;
}
